// The host side of the Bloom SDK: connects one app's full-view iframe (sandboxed, on the app frame origin) to the
// room. Everything the frame can do goes through here, and every message is checked:
// - it must come from this iframe's window (event.source), and be a well-formed SDK request;
// - the app is the one this frame was opened for, never one named in the message;
// - collections and actions must be declared in that app's manifest; writes are only ever action calls;
// - sizes and rates are capped;
// - edge-swipe "gesture" messages (frame/gestures.ts) only ever become touches for the phone's panel swipes.
// The frame gets only its own app's data, the people in the server (names and colours), and the theme.
import { fileRefs, mediaFields, mediaKindOf, SDK_GESTURES_PER_WINDOW, SDK_OPS, SDK_THEME_VARS, SDK_UPLOADS_PER_MIN, SDK_VERSION, type AppManifest, type Card, type FileRef, type MediaSize, type SdkMessage, type SdkMember, type SdkTheme, type SdkUser } from "@bloom/shared";
import type { Room } from "../realtime.ts";
import { FRAME_TOUCH, type FrameTouch } from "../mobile.ts";
import { configFailureText, fileUrl, getConfig, INLINE_ORIGINAL_MAX, uploadFile } from "../upload.ts";

/**
 * A file in one of this app's records, or null. A frame may only ever see its own app's files: the host fetches them
 * with the person's sign-in, so it must never fetch a key the frame merely names (another app's, a DM's photo).
 */
async function findFile(room: Room, app: AppManifest, key: string): Promise<FileRef | null> {
  for (const c of Object.keys(app.collections)) {
    const fields = mediaFields(app, c);
    if (!fields.length) continue;
    const records = room.appData.list(app.slug, c) ?? (await room.queryApp(app.slug, c)) ?? [];
    for (const r of records) for (const f of fields) for (const ref of fileRefs(r.data[f])) if (ref.key === key) return ref;
  }
  return null;
}

/** Where to get a file at a size, best first: display copies (shared/src/media.ts), then the original where it's fine. */
export function mediaSources(ref: FileRef, size: MediaSize): string[] {
  const url = fileUrl(ref.key);
  const kind = mediaKindOf(ref.type);
  if (size === "original") return [url];
  if (kind === "video") return [`${url}/poster.jpg`];
  // A big picture with no smaller copy is only loaded when someone asked to look at it (like chat's placeholder).
  const small = ref.size <= INLINE_ORIGINAL_MAX;
  return size === "small" ? [`${url}/w400.webp`, ...(small ? [url] : [])] : [`${url}/w1200.webp`, url];
}

export interface FrameOptions {
  app: AppManifest;
  room: Room;
  user: SdkUser;
  members: () => SdkMember[];
  /** Where cards the view posts go (the conversation the person came from). */
  channelId: () => string | null;
  route: Record<string, string>;
  openApp: (slug: string, route?: Record<string, string>) => void;
}

/** Messages bigger than this, or more than this many per 10 s, are dropped. */
const MAX_MESSAGE_CHARS = 64_000;
const MAX_PER_WINDOW = 300;

/** Bloom's current theme, for a frame: the resolved values of the theme variables. */
export function currentTheme(): SdkTheme {
  const cs = getComputedStyle(document.documentElement);
  const vars: Record<string, string> = {};
  for (const v of SDK_THEME_VARS) vars[v] = cs.getPropertyValue(v).trim();
  return { scheme: document.documentElement.dataset.theme === "dark" ? "dark" : "light", vars };
}

export function connectFrame(iframe: HTMLIFrameElement, o: FrameOptions) {
  const unsubs = new Map<string, () => void>();
  let stopListening: (() => void) | null = null;
  let windowStart = performance.now();
  let count = 0;
  let uploads: number[] = [];
  let gestureWindowStart = performance.now();
  let gestures = 0;
  const post = (m: SdkMessage) => {
    // The frame's origin is opaque (sandboxed without allow-same-origin), so "*" is the only target that reaches it.
    // What goes there is only ever this app's data; the host tears the frame down if it navigates (see AppFrame).
    iframe.contentWindow?.postMessage(m, "*");
  };
  const reply = (id: number, r: { ok: true; result: unknown } | { ok: false; error: string; errors?: Record<string, string>; code?: string }) =>
    post({ bloom: 1, re: id, ...r } as SdkMessage);

  const onMessage = async (e: MessageEvent) => {
    if (e.source !== iframe.contentWindow || !iframe.contentWindow) return;
    const d = e.data as Record<string, unknown> | null;
    if (!d || typeof d !== "object" || d.bloom !== 1 || typeof d.op !== "string") return;
    const now = performance.now();
    if (d.op === "gesture") {
      // A finger in the frame (frame/gestures.ts), for the phone's swipes between panels (mobile.ts useSwipe). It
      // can only ever move Bloom's own panels the way a finger on the page could; nothing else reads it.
      if (now - gestureWindowStart > 10_000) {
        gestureWindowStart = now;
        gestures = 0;
      }
      if (++gestures > SDK_GESTURES_PER_WINDOW) return;
      const { phase, x, y } = d;
      if ((phase !== "start" && phase !== "move" && phase !== "end") || !Number.isFinite(x) || !Number.isFinite(y)) return;
      if (Math.abs(x as number) > 1e5 || Math.abs(y as number) > 1e5) return;
      // Into this page's coordinates (the iframe moves with the chat while a panel follows the finger).
      const r = iframe.getBoundingClientRect();
      const detail: FrameTouch = { phase, x: r.left + (x as number), y: r.top + (y as number) };
      iframe.dispatchEvent(new CustomEvent(FRAME_TOUCH, { bubbles: true, detail }));
      return;
    }
    if (now - windowStart > 10_000) {
      windowStart = now;
      count = 0;
    }
    if (++count > MAX_PER_WINDOW) return;
    try {
      if (JSON.stringify(d).length > MAX_MESSAGE_CHARS) return;
    } catch {
      return;
    }
    if (d.op === "ready") {
      post({ bloom: 1, ev: "init", version: SDK_VERSION, app: o.app, user: o.user, theme: currentTheme(), route: o.route });
      return;
    }
    const id = d.id;
    if (typeof id !== "number" || !Number.isInteger(id) || !(SDK_OPS as readonly string[]).includes(d.op)) return;
    const app = o.app;
    const collection = typeof d.collection === "string" ? d.collection : "";
    const declared = Object.hasOwn(app.collections, collection);
    switch (d.op) {
      case "query": {
        if (!declared) return reply(id, { ok: false, error: "There's no such collection." });
        const records = await o.room.queryApp(app.slug, collection);
        return reply(id, records ? { ok: true, result: records } : { ok: false, error: "Couldn't load that." });
      }
      case "subscribe": {
        if (!declared) return reply(id, { ok: false, error: "There's no such collection." });
        if (!unsubs.has(collection)) unsubs.set(collection, o.room.watchCollection(app.slug, collection));
        stopListening ??= o.room.appData.onChange(app.slug, (c, rid, record) => {
          if (!unsubs.has(c)) return;
          if (rid === null) post({ bloom: 1, ev: "reset", collection: c, records: o.room.appData.list(app.slug, c) ?? [] });
          else post({ bloom: 1, ev: "change", collection: c, id: rid, ...(record ? { record } : {}) });
        });
        const records = o.room.appData.list(app.slug, collection) ?? (await o.room.queryApp(app.slug, collection)) ?? [];
        return reply(id, { ok: true, result: records });
      }
      case "unsubscribe": {
        unsubs.get(collection)?.();
        unsubs.delete(collection);
        return reply(id, { ok: true, result: null });
      }
      case "call": {
        const action = typeof d.action === "string" ? d.action : "";
        if (!Object.hasOwn(app.actions, action)) return reply(id, { ok: false, error: "This app has no such action." });
        const input = d.input && typeof d.input === "object" && !Array.isArray(d.input) ? (d.input as Record<string, unknown>) : {};
        // A draft's preview posts its cards into its blooming thread, so people see what the app will say in chat.
        const where = app.bloom?.draft ? o.channelId() : null;
        const r = await o.room.callAction(app.slug, action, input, where ? { channelId: where } : {});
        return reply(id, r.ok ? { ok: true, result: r.result } : { ok: false, error: r.message, ...(r.errors ? { errors: r.errors } : {}) });
      }
      case "currentUser":
        return reply(id, { ok: true, result: o.user });
      case "members":
        return reply(id, { ok: true, result: o.members() });
      case "postCard": {
        const channelId = o.channelId();
        if (!channelId) return reply(id, { ok: false, error: "There's no conversation to post in." });
        if (!d.card || typeof d.card !== "object") return reply(id, { ok: false, error: "That isn't a card." });
        const r = await o.room.postCard(app.slug, channelId, { ...(d.card as Card), app: app.slug });
        return reply(id, r.ok ? { ok: true, result: r.result } : { ok: false, error: r.message });
      }
      case "integration": {
        const integration = typeof d.integration === "string" ? d.integration : "";
        if (!(app.integrations ?? []).includes(integration)) return reply(id, { ok: false, error: "This app doesn't use that integration.", code: "not_declared" });
        const endpoint = typeof d.endpoint === "string" ? d.endpoint.slice(0, 40) : "";
        const params = d.params && typeof d.params === "object" && !Array.isArray(d.params) ? (d.params as Record<string, unknown>) : {};
        const r = await o.room.callIntegration(app.slug, integration, endpoint, params);
        return reply(id, r.ok ? { ok: true, result: r.data } : { ok: false, error: r.message, code: r.code });
      }
      case "upload": {
        const file = d.file;
        if (!(file instanceof Blob)) return reply(id, { ok: false, error: "That isn't a file." });
        if (!mediaKindOf(file.type)) return reply(id, { ok: false, error: "Only pictures and videos can be attached." });
        // A config that didn't load is not a size limit: say why (the next upload asks again).
        const config = await getConfig().catch((err) => configFailureText(err));
        if (typeof config === "string") return reply(id, { ok: false, error: `The upload didn't start: ${config}` });
        const max = config.maxFileBytes;
        if (file.size > max) return reply(id, { ok: false, error: `Files can be up to ${Math.round(max / 1024 / 1024)} MB.` });
        const t = Date.now();
        uploads = uploads.filter((x) => x > t - 60_000);
        if (uploads.length >= SDK_UPLOADS_PER_MIN) return reply(id, { ok: false, error: "That's a lot of uploads. Try again in a minute." });
        uploads.push(t);
        const name = (typeof d.name === "string" && d.name.trim() ? d.name : "file").slice(0, 120);
        try {
          const a = await uploadFile(o.room.serverId, file instanceof File ? file : new File([file], name, { type: file.type }), () => {}, { inMemory: true }).done;
          const ref: FileRef = { key: a.key, name: a.name, type: a.type, size: a.size, ...(a.width ? { width: a.width } : {}), ...(a.height ? { height: a.height } : {}) };
          return reply(id, { ok: true, result: ref });
        } catch (err) {
          return reply(id, { ok: false, error: err instanceof Error ? err.message : "The upload failed." });
        }
      }
      case "media": {
        const key = typeof d.key === "string" ? d.key.slice(0, 300) : "";
        const size: MediaSize = d.size === "large" || d.size === "original" ? d.size : "small";
        const ref = key ? await findFile(o.room, app, key) : null;
        if (!ref) return reply(id, { ok: false, error: "That isn't one of this app's files." });
        for (const src of mediaSources(ref, size)) {
          try {
            const res = await fetch(src, { credentials: "same-origin" });
            if (res.ok) return reply(id, { ok: true, result: await res.blob() });
          } catch {
            // try the next source
          }
        }
        return reply(id, { ok: false, error: "That file can't be shown." });
      }
      case "openApp": {
        const slug = typeof d.slug === "string" ? d.slug : "";
        if (!o.room.getSnapshot().apps.some((a) => a.slug === slug)) return reply(id, { ok: false, error: "There's no such app." });
        const route = d.route && typeof d.route === "object" ? Object.fromEntries(Object.entries(d.route as Record<string, unknown>).slice(0, 4).map(([k, v]) => [k.slice(0, 20), String(v).slice(0, 64)])) : undefined;
        o.openApp(slug, route);
        return reply(id, { ok: true, result: null });
      }
    }
  };
  addEventListener("message", onMessage);
  return {
    theme: (t: SdkTheme) => post({ bloom: 1, ev: "theme", theme: t }),
    route: (r: Record<string, string>) => post({ bloom: 1, ev: "route", route: r }),
    dispose() {
      removeEventListener("message", onMessage);
      for (const u of unsubs.values()) u();
      unsubs.clear();
      stopListening?.();
    },
  };
}
